import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export type ServiceProduct = {
  id: number;
  name: string;
  description: string | null;
  price: number;
  stock: number | null;
  currency: string;
  discount_enabled: boolean;
  discount_price: number | null;
  media: { url: string; type: string }[];
  status: string;
  category_id: number | null;
  product_categories: { id: number; name: string; sort_order: number } | null;
};

function normalize(row: any): ServiceProduct {
  return {
    id: Number(row.id),
    name: row.name ?? "",
    description: row.description ?? null,
    price: Number(row.price ?? 0),
    stock: row.stock ?? null,
    currency: row.currency ?? "USD",
    discount_enabled: !!row.discount_enabled,
    discount_price: row.discount_price != null ? Number(row.discount_price) : null,
    media: Array.isArray(row.media) ? row.media : [],
    status: row.status ?? "draft",
    category_id: row.category_id ?? null,
    product_categories: row.product_categories ?? null,
  };
}

export function useServiceProducts() {
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("status", "active")
          .order("id", { ascending: true });
        if (err) throw err;
        if (mounted) setProducts((data ?? []).map(normalize));
      } catch (e: any) {
        if (mounted) setError(e.message ?? "Failed to load services");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return { products, loading, error };
}

export function useServiceProduct(id: number | string | undefined) {
  const [product, setProduct] = useState<ServiceProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id == null) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("id", Number(id))
          .maybeSingle();
        if (err) throw err;
        if (mounted) setProduct(data ? normalize(data) : null);
      } catch (e: any) {
        if (mounted) setError(e.message ?? "Failed to load service");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  return { product, loading, error };
}